import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar/constants'
import type {CollapsibleLeftPaneConfig} from '@github-ui/three-panes-layout'
import type {ReactNode} from 'react'

/**
 * Shared sidebar configuration for every page that renders the issues sidebar
 * inside a `ThreePanesLayout`.
 *
 * - `issue_pinned_views` OFF → no sidebar (returns {}).
 * - `issue_pinned_views` ON → render the sidebar inside a `CollapsibleSidebar`
 *   with a persisted toggle so the collapse state is shared across the issues,
 *   milestones, labels and views pages.
 *
 * `initialExpanded` is the server-read cookie seed (see
 * `useInitialSidebarExpanded`); it keeps the collapsed sidebar from flashing
 * open on hard navigation.
 */
export function useRepoIssuesSidebarPanes(
  renderSidebar: (collapsible?: boolean) => ReactNode,
  initialExpanded?: boolean,
): {
  collapsibleLeftPane?: CollapsibleLeftPaneConfig
} {
  const pinnedViews = isFeatureEnabled('issue_pinned_views')
  if (!pinnedViews) {
    return {}
  }

  return {
    collapsibleLeftPane: {
      storageKey: SIDEBAR_STORAGE_KEY,
      initialExpanded,
      expandedWidth: 'small',
      render: () => <>{renderSidebar(true)}</>,
      renderFooter: ({isExpanded, toggle}) => (
        <>
          <CollapsibleFeedbackLink />
          <CollapsibleSidebar.Toggle isExpanded={isExpanded} onToggle={toggle} />
        </>
      ),
    },
  }
}
